import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { 
  Elements, 
  PaymentElement, 
  LinkAuthenticationElement, 
  useStripe, 
  useElements 
} from "@stripe/react-stripe-js";
import { supabase } from "../supabaseClient";
import { generateSCProgram } from "./SCWorkoutGenerator";

const stripePromise = loadStripe("pk_live_eexDwthB54cR1fRWFkSIMn9P");
// const stripePromise = loadStripe("pk_test_XWWAJyndmHBL9Y8LcFZFgAQd");

const CheckoutForm = ({ 
  user, 
  program, 
  numWorkouts,
  trainingDays,
  workoutLength,
  modeledPlayer,
  onProgramGenerated, 
  setStatus,
  status 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [email, setEmail] = useState(user?.email || '');

  console.log("SC PURCHASE BUTTON", user)

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setStatus("Processing payment...");
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { 
        return_url: window.location.href,
        receipt_email: email,
      },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message);
      setStatus("Payment failed.");
      return;
    }

    // --- PAYMENT SUCCESS: TRIGGER GENERATOR ---
    setStatus("Payment successful! Starting AI...");
    
    try {
      // Handle both object (user.id) and string (user) cases safely
      const userId = user?.id || user;

      const result = await generateSCProgram({
        userId,
        program,
        numWorkouts,
        trainingDays,
        workoutLength,
        modeledPlayer,
        supabase,
        setStatusMessage: setStatus
      });

      setStatus("Done!");
      if (onProgramGenerated) {
        onProgramGenerated(result);
      }

    } catch (genError) {
      console.error("Gen Error:", genError);
      setErrorMessage("Payment worked, but generation failed. Support has been notified.");
      setStatus("Error generating program.");
    }
  };

  return (
    <div className="relative mt-2">
      {/* Loading Overlay */}
      {(status && (status.includes("Processing") || status.includes("AI") || status.includes("Setting") || status.includes("Analyzing"))) && (
        <div className="absolute inset-0 z-20 bg-white/90 flex flex-col items-center justify-center rounded-lg backdrop-blur-[2px]">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
          <span className="text-xs font-semibold text-blue-700 animate-pulse text-center px-2">{status}</span>
        </div>
      )}

      <form onSubmit={handleConfirm} className="flex flex-col gap-3">
        
        {/* Helper Badge */}
        <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-md border border-blue-100">
          <div className="bg-blue-600 text-white rounded-full p-1">
             <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>
          <p className="text-[11px] text-blue-800 leading-tight">
            <strong>Fast Checkout:</strong> Confirm your email below to auto-fill your saved payment details.
          </p>
        </div>

        {/* Link Authentication (Email) */}
        <LinkAuthenticationElement
          id="link-authentication-element"
          onChange={(e) => setEmail(e.value.email)}
        />
        
        {/* Payment Fields (Card) */}
        <PaymentElement 
          options={{
            layout: "tabs",
            fields: { billingDetails: { email: 'never' } }
          }}
        />

        <button 
          disabled={!stripe || status.includes("Processing")}
          type="submit"
          className="w-full mt-2 bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 shadow-sm"
        >
          {status.includes("Processing") ? "Processing..." : "Pay $9.99"}
        </button>
      </form>
      
      {errorMessage && (
        <div className="text-red-500 text-xs mt-2 p-2 bg-red-50 rounded text-center border border-red-100">
          {errorMessage}
        </div>
      )}
    </div>
  );
};


export default function SCPurchaseButton({ 
  user, // Renamed from userId to allow object access
  selectedProgram, 
  numWorkouts, 
  trainingDays,
  workoutLength, 
  modeledPlayer,
  onProgramGenerated 
}) {
  const [clientSecret, setClientSecret] = useState(null);
  const [status, setStatus] = useState("");

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe', 
      variables: {
        colorPrimary: '#1d4ed8', 
        borderRadius: '8px',
        fontSizeBase: '14px',
      },
    },
    // Auto-fill the email for Link
    defaultValues: {
      billingDetails: {
        email: user?.email || "", 
      }
    }
  };
  

  useEffect(() => {
    supabase.functions.invoke("create-payment-intent", { 
      // Ensure this amount matches what you want to charge ($9.99 = 999)
      body: { amount: 999, currency: "usd" } 
    })
    .then(({ data, error }) => {
      if (data?.clientSecret) setClientSecret(data.clientSecret);
      if (error) console.error("Stripe Intent Error:", error);
    });
  }, []);

  if (!clientSecret) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm h-72 animate-pulse flex flex-col gap-4">
           {/* Header Skeleton */}
           <div className="h-4 w-32 bg-gray-200 rounded mx-auto"></div>
           {/* Form Skeleton */}
           <div className="h-12 w-full bg-gray-100 rounded"></div>
           <div className="h-32 w-full bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <Elements stripe={stripePromise} options={options}>
        <div className="relative border border-gray-200 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-all">
          <div className="text-center mb-4">
            <p className="text-gray-900 font-semibold text-sm">Get Monthly SC Plan</p>
            <p className="text-xs text-gray-500">AI-generated strength & conditioning</p>
          </div>
          
          <CheckoutForm 
            user={user} 
            program={selectedProgram} 
            numWorkouts={numWorkouts}
            trainingDays={trainingDays}
            workoutLength={workoutLength}
            modeledPlayer={modeledPlayer}
            onProgramGenerated={onProgramGenerated}
            setStatus={setStatus} 
            status={status}
          />
          
          <p className="text-center text-[10px] text-gray-400 mt-4 flex items-center justify-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm0-4H9V7h2v5z"/></svg>
            Secure payment powered by Stripe
          </p>
        </div>
      </Elements>
    </div>
  );
}
